/**
 * 通用的 API 事件分发链接
 *
 * 这个模块提供了一个通用的 TRPC 链接，可以在特定 API 调用完成后自动触发自定义事件。
 * 通过修改 API_EVENT_CONFIGS 配置来添加新的事件和 API 路径映射。
 *
 * @example 配置示例
 * ```typescript
 * import { API_EVENT_CONFIGS, CUSTOM_EVENTS } from '@/utils/apiEventDispatchLink'
 *
 * // 添加到现有的 AUTO_UPDATE_SUB_USAGE 配置
 * API_EVENT_CONFIGS[CUSTOM_EVENTS.AUTO_UPDATE_SUB_USAGE].push({
 *   path: 'user.updateSubscription',
 *   type: 'mutation',
 *   delay: 200,
 *   shouldTrigger: (value) => value.result?.success === true,
 *   eventData: (value) => ({ userId: value.result?.userId })
 * })
 * ```
 */

import { observable } from '@trpc/server/observable'

import type { TRPCLink } from '@trpc/client'
import type { WebRouter } from '@loc/server'

import { CUSTOM_EVENTS, type CustomEventName } from '@/constants/events'

// API 路径配置的回调函数类型
type ApiPathCallback = (value: any) => boolean

// API 路径配置（不包含事件名称）
export interface ApiPathConfig {
  path: string
  type: 'query' | 'mutation'
  // 可选的自定义判断回调，返回 true 表示需要触发事件
  shouldTrigger?: ApiPathCallback
  // 事件延迟时间（毫秒），默认为 100ms
  delay?: number
  // 传递给事件的额外数据
  eventData?: (value: any) => any
}

// 事件配置映射：事件名称 -> API 路径配置数组
export type EventConfigMap = Record<CustomEventName, ApiPathConfig[]>

// 事件配置映射 - 可以直接修改此对象来添加新的事件配置
export const API_EVENT_CONFIGS: EventConfigMap = {
  [CUSTOM_EVENTS.AUTO_UPDATE_SUB_USAGE]: [
    {
      path: 'task.create',
      type: 'mutation',
    },
    {
      path: 'music.polling',
      type: 'mutation',
      shouldTrigger: (value) => {
        if (!('result' in value) || !value.result) return false
        // tRPC link 中 value.result 结构为 { data: [...], type: 'data' }
        const result = value.result?.data
        return (
          Array.isArray(result) &&
          result.some(
            (item: any) => item.status === 'Failed' || item.status === 'Fatal',
          )
        )
      },
    },
    {
      path: 'music.retry',
      type: 'mutation',
    },
    {
      path: 'cover.createTask',
      type: 'mutation',
    },
    {
      path: 'tts.createTask',
      type: 'mutation',
    },
    {
      path: 'reference.polling',
      type: 'mutation',
      shouldTrigger: (value) => {
        if (!('result' in value) || !value.result) return false
        // tRPC link 中 value.result 结构为 { data: {...}, type: 'data' }
        const result = value.result?.data
        return !!result?.detectedLyrics
      },
    },
  ],
}

// 通用的 API 事件分发 Link
export const apiEventDispatchLink: TRPCLink<WebRouter> = () => {
  return ({ next, op }) => {
    return observable((observer) => {
      const unsubscribe = next(op).subscribe({
        next(value) {
          // 遍历所有事件配置
          Object.entries(API_EVENT_CONFIGS).forEach(
            ([eventName, pathConfigs]) => {
              // 查找匹配当前 API 调用的路径配置
              const matchingConfigs = pathConfigs.filter(
                (config) => config.path === op.path && config.type === op.type,
              )

              matchingConfigs.forEach((config) => {
                let shouldTriggerEvent = false

                // 如果有自定义判断回调，使用回调来判断
                if (config.shouldTrigger) {
                  shouldTriggerEvent = config.shouldTrigger(value)
                } else {
                  // 默认判断：检查是否有 result 且 result 为真值
                  shouldTriggerEvent = 'result' in value && !!value.result
                }

                if (shouldTriggerEvent) {
                  const delay = config.delay ?? 100
                  const eventData = config.eventData
                    ? config.eventData(value)
                    : undefined

                  // 延迟执行，避免阻塞当前请求
                  setTimeout(() => {
                    // 触发自定义事件
                    const customEvent = new CustomEvent(
                      eventName as CustomEventName,
                      {
                        detail: eventData,
                      },
                    )
                    window.dispatchEvent(customEvent)
                  }, delay)
                }
              })
            },
          )

          observer.next(value)
        },
        error(error) {
          observer.error(error)
        },
        complete() {
          observer.complete()
        },
      })
      return unsubscribe
    })
  }
}
